import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, LayoutDashboard, ShieldCheck, ExternalLink, CreditCard, Users, Code2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../Logo";

export const PortalShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const links = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/workspace", label: "Team", icon: Users },
    { to: "/developers", label: "Developers", icon: Code2 },
    { to: "/billing", label: "Billing", icon: CreditCard },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : []),
  ];

  return (
    <div className="min-h-screen bg-secondary">
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden items-center gap-1 md:flex">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`portal-nav-${l.label.toLowerCase()}`}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    pathname === l.to ? "bg-secondary text-brand" : "text-steel hover:text-brand"
                  }`}
                >
                  <l.icon className="h-4 w-4" /> {l.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 text-sm text-muted-foreground hover:text-brand sm:inline-flex">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-navy">{user?.name || user?.email}</div>
              <div className="text-xs capitalize text-muted-foreground">{user?.role}</div>
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand font-display text-sm font-bold text-white">
                {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
              </span>
            )}
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-steel hover:text-destructive" title="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-8 lg:py-10">{children}</main>
    </div>
  );
};
